import React from 'react';
import { METHOD_COLORS, METHOD_SHORT_LABELS } from '../../lib/gis-types';

/**
 * Builds the SurveyMap `popupInfo` for the current selection.
 * Priority: anomaly || line || xyz row.
 */
export function buildPopupInfo({ selectedFeature, selectedAnomaly, xyzPopup, C, navigate, viewer3dHref = '/3d?focus=1' }) {
  // Popup content builders
  const linePopup = selectedFeature && !selectedFeature.isBorehole ? {
    longitude: selectedFeature.longitude,
    latitude: selectedFeature.latitude,
    content: (
      <div style={{ padding: 4, minWidth: 200 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: METHOD_COLORS[selectedFeature.method] || '#00d4ff' }}>
            {selectedFeature.name}
          </span>
        </div>
        {(selectedFeature.length || selectedFeature.electrodes) && (
          <div style={{ fontSize: 12, color: C.text2, marginBottom: 8 }}>
            {selectedFeature.length ? `${selectedFeature.length}m line` : ''}{selectedFeature.length && selectedFeature.electrodes ? ' · ' : ''}{selectedFeature.electrodes ? `${selectedFeature.electrodes} electrodes` : ''}
          </div>
        )}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
          <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 99, background: C.accentBg, color: C.accent, border: `1px solid ${C.accentBorder}` }}>
            {METHOD_SHORT_LABELS[selectedFeature.method] || selectedFeature.method}
          </span>
          <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 99, background: 'rgba(16,185,129,0.12)', color: '#34d399', border: '1px solid rgba(16,185,129,0.25)' }}>
            RMS 1.8%
          </span>
          <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 99, background: 'rgba(16,185,129,0.12)', color: '#34d399', border: '1px solid rgba(16,185,129,0.25)' }}>
            QC Pass
          </span>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {selectedFeature._geometry?.type === 'LineString' && (
            <button
              onClick={() => {
                sessionStorage.setItem('viewer3d-focus', JSON.stringify({
                  name: selectedFeature.name,
                  method: selectedFeature.method,
                  coordinates: selectedFeature._geometry.coordinates,
                  clickPoint: [selectedFeature.longitude, selectedFeature.latitude],
                }));
                navigate(viewer3dHref);
              }}
              style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, background: 'linear-gradient(135deg,#00b8de,#3b82f6)', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
              🏔 View in 3D
            </button>
          )}
          <button
            onClick={() => window.open('https://erimager-bert.pages.dev/', '_blank', 'noopener')}
            style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, background: C.accentBg, color: C.accent, border: `1px solid ${C.accentBorder}`, cursor: 'pointer' }}>
            Open in EarthImager ↗
          </button>
        </div>
      </div>
    ),
  } : null;

  const anomalyPopup = selectedAnomaly ? {
    longitude: selectedAnomaly.longitude,
    latitude: selectedAnomaly.latitude,
    content: (
      <div style={{ padding: 4, minWidth: 220 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: selectedAnomaly.color, marginBottom: 6 }}>
          Anomaly {selectedAnomaly.label}
        </div>
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{selectedAnomaly.type}</div>
        <div style={{ fontSize: 11, color: C.text2, lineHeight: 1.5, marginBottom: 8 }}>{selectedAnomaly.desc}</div>
        <div style={{ display: 'flex', gap: 6 }}>
          <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 99, background: `${selectedAnomaly.color}22`, color: selectedAnomaly.color, border: `1px solid ${selectedAnomaly.color}44` }}>
            {selectedAnomaly.severity} severity
          </span>
          <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 99, background: 'rgba(167,139,250,0.12)', color: C.purple, border: '1px solid rgba(167,139,250,0.25)' }}>
            AI flagged
          </span>
        </div>
      </div>
    ),
  } : null;

  // XYZ full-row popup
  const xyzRowPopup = xyzPopup ? {
    longitude: xyzPopup.longitude,
    latitude: xyzPopup.latitude,
    content: (
      <div style={{ padding: 4, minWidth: 200, maxHeight: 260, overflow: 'auto' }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.accent, marginBottom: 6 }}>📍 Data Point</div>
        <table style={{ fontSize: 10, borderCollapse: 'collapse', width: '100%' }}>
          <tbody>
            {Object.entries(xyzPopup.properties || {}).filter(([k]) => k !== '_row' && k !== '_excluded').map(([k, v]) => (
              <tr key={k}>
                <td style={{ padding: '2px 8px 2px 0', color: xyzPopup.zFields?.includes(k) ? '#fbbf24' : C.text3, fontWeight: xyzPopup.zFields?.includes(k) ? 600 : 400 }}>{k}</td>
                <td style={{ padding: '2px 0', color: C.text0, fontFamily: 'monospace' }}>{typeof v === 'number' ? v.toLocaleString() : String(v)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ),
  } : null;

  return anomalyPopup || linePopup || xyzRowPopup;
}
